import * as Dom from "./dom";
import * as Data from "./data";
import * as Display from "./display";
import * as Events from "./events";
import { TractionType, Model, ListPair, Shift, ShiftType, Course, Trip, Vehicle } from "./entities";
import { tractionToHuman, tractionToSA, ageToHuman, idToTraction, getHhMm, offsetMidnight } from "./extensions";
import { renderVehiclePhoto } from "./photo";

import type { ApiConnectionError } from "./services/availability";

export let darkenedElement: HTMLElement;
export let vehiclesPage: HTMLDivElement;
export let filtersEnabled: boolean;

export function addVehiclesPage(acPercentage: number, averageDelay: number) {
  vehiclesPage = (Dom.templates.vehiclesPage.content.cloneNode(true) as Element).firstElementChild as HTMLDivElement;

  vehiclesPage.querySelector<HTMLButtonElement>(".reset-filters").onclick = async () => await Events.resetFiltersClicked();
  vehiclesPage.querySelector<HTMLButtonElement>(".buttons .faq").onclick = async () => Display.addFaqView();

  Array.from(vehiclesPage.querySelectorAll(".filter input") as NodeListOf<HTMLInputElement>).forEach(async (input) => {
    input.oninput = async () => await Events.filterInputsChanged();
  });

  vehiclesPage.querySelector<HTMLElement>(".ac-percentage").textContent = acPercentage.toString();
  vehiclesPage.querySelector<HTMLElement>(".average-delay").textContent = averageDelay.toString();
  if (Data.cachedSnapshot) {
    let parts = ageToHuman(Data.cachedSnapshot.age);
    vehiclesPage.querySelector<HTMLElement>(".data-age").textContent = (parts[0] as number).toString();
    vehiclesPage.querySelector<HTMLElement>(".data-age-unit").textContent = parts[1] as string;

    Display.addDataAge();
  }

  Dom.content.append(vehiclesPage);
}

export function resetDarkenedElement() {
  if (!darkenedElement) return;

  lightenElement(darkenedElement);
  darkenedElement = null;
}

export function addVehicleView(
  line: string,
  heading: string,
  seznamAutobusu: string,
  provoz: string,
  delay: number,
  paint: string,
  from: string,
  to: string,
  lastStop: string,
  nextStop: string,
  vehicle?: Vehicle
) {
  Dom.dialogs.vehicleView.tag.textContent = line;
  Dom.dialogs.vehicleView.heading.textContent = heading;
  Dom.dialogs.vehicleView.seznamAutobusu.href = seznamAutobusu;

  // Handle imported vehicles that might not have connectionId
  if (vehicle?.isImported && !vehicle.connectionId) {
    Dom.dialogs.vehicleView.provoz.href = "";
    Dom.dialogs.vehicleView.provoz.style.pointerEvents = "none";
    Dom.dialogs.vehicleView.provoz.style.opacity = "0.5";
  } else {
    Dom.dialogs.vehicleView.provoz.href = provoz;
    Dom.dialogs.vehicleView.provoz.style.pointerEvents = "auto";
    Dom.dialogs.vehicleView.provoz.style.opacity = "1";
  }
  if (delay == 0) {
    Dom.dialogs.vehicleView.delay.innerHTML = `<span class="safety">Spoj jede včas</span>`;
  } else {
    Dom.dialogs.vehicleView.delay.innerHTML = `+ <span class="danger"><b>${delay}</b> min.</span>`;
  }
  if (paint) {
    Dom.dialogs.vehicleView.paintContainer.classList.remove("invisible");
    Dom.dialogs.vehicleView.paint.textContent = paint;
  } else {
    Dom.dialogs.vehicleView.paintContainer.classList.add("invisible");
    Dom.dialogs.vehicleView.paint.textContent = "";
  }

  // Handle imported vehicles differently
  if (vehicle?.isImported) {
    // Hide path, last/next stop information
    Dom.dialogs.vehicleView.path.textContent = "";
    Dom.dialogs.vehicleView.lastStop.textContent = "";
    Dom.dialogs.vehicleView.nextStop.textContent = "";

    // Show imported vehicle notice instead of loading course/shift/connection info
    Dom.dialogs.vehicleView.order.innerHTML = `<div class="imported-vehicle-notice">Detail vozidla je nedostupný kvůli technické limitaci na straně DP.</div>`;
    Dom.dialogs.vehicleView.shift.innerHTML = "";
    Dom.dialogs.vehicleView.connectionTableBody.innerHTML = "";
    while (Dom.dialogs.vehicleView.connectionTableBody.firstElementChild) Dom.dialogs.vehicleView.connectionTableBody.firstElementChild.remove();
  } else {
    Dom.dialogs.vehicleView.path.textContent = `${from} > ${to}`;
    Dom.dialogs.vehicleView.lastStop.textContent = lastStop;
    Dom.dialogs.vehicleView.nextStop.textContent = nextStop ?? lastStop;

    Dom.dialogs.vehicleView.order.innerHTML = `Načítání`;
    Dom.dialogs.vehicleView.shift.innerHTML = `Načítání`;
    Dom.dialogs.vehicleView.connectionTableBody.innerHTML = "Načítání";
    while (Dom.dialogs.vehicleView.connectionTableBody.firstElementChild) Dom.dialogs.vehicleView.connectionTableBody.firstElementChild.remove();
  }

  Dom.dialogs.vehicleView.self.showModal();
  updateDialogScrollLock();

  if (vehicle) {
    renderVehiclePhoto(Dom.dialogs.vehicleView.lastPhotoBlock, Dom.dialogs.vehicleView.lastPhoto, vehicle);
  } else {
    Dom.dialogs.vehicleView.lastPhotoBlock.classList.add("invisible");
    Dom.dialogs.vehicleView.lastPhoto.replaceChildren();
  }

  Dom.dialogs.vehicleView.seznamAutobusu.blur();
}

function getShiftTypeLabel(type: ShiftType): string {
  switch (type) {
    case ShiftType.Morning:
      return "ranní";
    case ShiftType.FullDay:
      return "túra";
    case ShiftType.Split:
      return "šejdr";
    case ShiftType.Afternoon:
      return "odpolední";
    case ShiftType.Night:
      return "noční";
  }
}

export function addVehicleCourse(course: Course, shift?: Shift) {
  if (!course) {
    Dom.dialogs.vehicleView.order.innerHTML = `Chyba při načítání`;
    return;
  }

  let shiftType = shift ? ` (${getShiftTypeLabel(shift.type)})` : "";

  Dom.dialogs.vehicleView.order.innerHTML = `Kurz: <code>${course.originalLine}/${course.order}</code>${shiftType}`;
}

export function addVehicleViewShift(shift: Shift) {
  if (!shift) {
    Dom.dialogs.vehicleView.shift.innerHTML = `Chyba při načítání`;
    return;
  }

  Dom.dialogs.vehicleView.shift.innerHTML = `Zátah: <code>${getHhMm(shift.endsAt)}</code>`;
}

export function addVehicleViewTrip(lastStopName: string, trip: Trip) {
  if (!trip) {
    Dom.dialogs.vehicleView.connectionTableBody.innerHTML = `Chyba při načítání`;
    return;
  } else {
    Dom.dialogs.vehicleView.connectionTableBody.innerHTML = "";
  }

  for (let departure of trip.departures) {
    let stopNameElement = document.createElement("td");
    stopNameElement.textContent = `${departure.stopName}${departure.isOnRequest ? " (Z)" : ""}`;

    let stopDepartureElement = document.createElement("td");
    stopDepartureElement.innerHTML = `<code>${offsetMidnight(getHhMm(departure.departuresAt))}</code>`;

    let tableRowElement = document.createElement("tr");
    if (departure.stopName == lastStopName) tableRowElement.classList.add("highlighted-stop");

    tableRowElement.appendChild(stopNameElement);
    tableRowElement.appendChild(stopDepartureElement);

    Dom.dialogs.vehicleView.connectionTableBody.appendChild(tableRowElement);
  }
}

export function removeVehicleView() {
  Dom.dialogs.vehicleView.self.close();
}

export function renderVehicles(vehicles: Vehicle[]) {
  let vehiclesView = vehiclesPage.querySelector(".vehicles-view");
  vehiclesView.replaceChildren();

  for (let tractionType of Object.values(TractionType)) {
    let modelsOfTraction = Data.carrier.filter((x) => x.tractionType === tractionType);
    let vehiclesOfTraction = vehicles.filter((x) => x.tractionType == tractionType);

    addTraction(tractionType, modelsOfTraction, vehiclesOfTraction);
  }
}

export function addTraction(type: TractionType, models: Model[], vehicles: Vehicle[]) {
  console.info(`Adding traction for ${type} with ${models.length} models and ${vehicles.length} vehicles`);

  let tractionClone = (Dom.templates.traction.content.cloneNode(true) as Element).firstElementChild as HTMLDivElement;

  (tractionClone.querySelector(".icon") as HTMLImageElement).src = `./assets/${TractionType[type].toString().toLowerCase()}.svg`;
  (tractionClone.querySelector(".title") as HTMLHeadingElement).textContent = `${tractionToHuman(type)} (${vehicles.length})`;
  let modelsDiv = tractionClone.querySelector(".models") as HTMLDivElement;

  let results: ListPair[] = [];

  for (let model of models) {
    let vehiclesOfModel = vehicles.filter((x) => model.registrationNumbers.includes(x.registrationNumber));
    if (vehiclesOfModel.length == 0) continue;

    vehiclesOfModel = vehiclesOfModel.sort((a, b) => {
      return a.registrationNumber - b.registrationNumber;
    });

    results.push({
      model,
      vehicles: vehiclesOfModel
    });
  }

  results = results.sort((a, b) => {
    return b.vehicles.length - a.vehicles.length;
  });

  let unassigned = vehicles.filter((original) => !results.some((result) => result.vehicles.some((vehicle) => vehicle.registrationNumber == original.registrationNumber)));
  if (unassigned && unassigned.length > 0) {
    console.info(`Unassigned for traction type ${type}`, unassigned);
  }

  let unusual = vehicles.filter((original) => {
    let vehicleType = idToTraction(Number(original.id.substring(0, 1)));
    let tractionType = original.lineMeta.tractionType;

    return vehicleType != tractionType;
  });

  if (unusual && unusual.length > 0) {
    console.info(`Unusual assign for traction type ${type}`, unusual);
  }

  for (let pair of results) {
    console.info(`Model ${pair.model.name} has ${pair.vehicles.length} vehicles on track`);

    let modelClone = (Dom.templates.model.content.cloneNode(true) as Element).firstElementChild as HTMLDivElement;

    (modelClone.querySelector(".name") as HTMLHeadingElement).textContent = `${pair.model.name} (${pair.vehicles.length})`;
    let vehicleTags = modelClone.querySelector(".vehicle-tags") as HTMLDivElement;

    for (let vehicle of pair.vehicles) {
      let tractionType = idToTraction(Number(vehicle.id[0]));
      let vehicleTagClone = (Dom.templates.vehicleTag.content.cloneNode(true) as Element).firstElementChild as HTMLDivElement;

      vehicleTagClone.dataset.line = vehicle.lineMeta.name;
      vehicleTagClone.dataset.registrationNumber = vehicle.registrationNumber.toString();
      (vehicleTagClone.querySelector(".value") as HTMLSpanElement).textContent = vehicle.registrationNumber.toString();

      let paint = Data.paints.find((x) => x.vehicleId === vehicle.id);
      if (paint) {
        vehicleTagClone.classList.add("paint", paint.paintId);
      }

      vehicleTagClone.onclick = async () => {
        Display.darkenElement(vehicleTagClone);

        Display.addVehicleView(
          vehicle.lineMeta.name,
          pair.model.name,
          `https://seznam-autobusu.cz/seznam?evc=${vehicle.registrationNumber}&tractionId=${tractionToSA(tractionType)}&iddopravce=71`,
          vehicle.connectionId ? `https://jizdnirady.pmdp.cz/provoz/?id=${vehicle.connectionId}` : "",
          vehicle.delayMinutes,
          paint?.name,
          vehicle.startName || "",
          vehicle.destinationName || "",
          vehicle.lastStopName || "",
          vehicle.nextStopName,
          vehicle
        );

        Events.vehicleDialogOpened(vehicle);
      };

      vehicleTags.append(vehicleTagClone);
    }

    modelsDiv.append(modelClone);
  }

  vehiclesPage.querySelector(".vehicles-view").append(tractionClone);
}

export function setVehicleCount(value: number) {
  Dom.vehicleCount.textContent = ` (${value})`;
}

export function setFilterResults(value: number) {
  vehiclesPage.querySelector(".filter-results").textContent = ` (${value})`;
}

export function deleteFilterResults() {
  vehiclesPage.querySelector(".filter-results").textContent = "";
}

export function darkenElement(element: HTMLElement) {
  element.classList.add("dark");
  darkenedElement = element;
}

export function lightenElement(element: HTMLElement) {
  element.classList.remove("dark");
}

export function clearFilters() {
  if (filtersEnabled) {
    clearFilterInputs();
    resetFilters();

    filtersEnabled = false;
  } else if (!filtersEnabled && Data.cachedSnapshot.isOld) {
    disableOldPreview();
  }
}

export function clearFilterInputs() {
  let inputs = vehiclesPage.querySelectorAll(".filter input") as NodeListOf<HTMLInputElement>;
  for (let input of Array.from(inputs)) input.value = "";
}

export function applyFilters() {
  filtersEnabled = true;

  let line = (vehiclesPage.querySelector("input[name='line-filter']") as HTMLInputElement).value.trim();
  let course = (vehiclesPage.querySelector("input[name='course-filter']") as HTMLInputElement).value.trim();

  if (line == "" && course == "") {
    resetFilters();
    return;
  }

  let filtered = Data.vehicles;
  if (line != "") filtered = filtered.filter((x) => x.lineMeta.name == line);
  if (course != "") filtered = filtered.filter((x) => x.order != null && x.order.toString() == course);

  renderVehicles(filtered);
  setFilterResults(filtered.length);
}

export function resetFilters() {
  deleteFilterResults();
  renderVehicles(Data.vehicles);
}

export function disableOldPreview() {
  let url = new URL(window.location.href);
  url.search = "";

  window.location.href = url.toString();
}

export function addLoading() {
  Dom.status.classList.remove("invisible");
}

export function removeLoading() {
  Dom.status.classList.add("invisible");
}

export function setLoadingMessage(msg: string) {
  Dom.status.textContent = msg;
}

export function setApiConnectionError(error: ApiConnectionError | null) {
  Dom.retryApi.classList.toggle("invisible", !error);
  if (error) {
    setLoadingMessage(`❌ ${error.message}`);
    addLoading();
  } else {
    removeLoading();
  }
}

export function addDisclaimer() {
  Dom.disclaimer.classList.remove("invisible");
}

export function removeDisclaimer() {
  Dom.disclaimer.classList.add("invisible");
}

export function addDataAge() {
  vehiclesPage.querySelector(".data-age-container").classList.remove("invisible");
}

export function removeDataAge() {
  vehiclesPage.querySelector(".data-age-container").classList.add("invisible");
}

export function addFaqView() {
  darkenElement(document.querySelector<HTMLButtonElement>(".buttons .faq"));
  Dom.dialogs.faqView.self.showModal();
  updateDialogScrollLock();
}

export function removeFaqView() {
  Dom.dialogs.faqView.self.close();
}

export function updateDialogScrollLock() {
  const anyDialogOpen = Array.from(document.querySelectorAll<HTMLDialogElement>("dialog")).some((dialog) => dialog.open);
  document.body.classList.toggle("dialog-open", anyDialogOpen);
}
